import React, { useEffect, useState, useCallback } from 'react';
import { UserCheck, Plus, Edit2, Trash2, AlertCircle, Filter, Search, Download, Upload, Phone, Users, XCircle, CheckSquare } from 'lucide-react';
import ServiceContainer from '../../../core/services/ServiceContainer';
import { useContactStore } from '../../../core/services/ContactStore';
import { useDamStore } from '../../../core/services/DamStore';
import { useClientStore } from '../../../core/services/ClientStore';
import { useAuthStore } from '../../../core/services/AuthStore';
import { IContact, ICreateContactRequest } from '../../../core/interfaces/IContact';
import { Card, CardContent } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Label } from '../../../shared/components/ui/label';
import { Badge } from '../../../shared/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../shared/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../shared/components/ui/select';

const serviceContainer = ServiceContainer.getInstance();
const contactService = serviceContainer.getContactService();
const damService = serviceContainer.getDamService();
const clientService = serviceContainer.getClientService();

export const ContactsPage: React.FC = () => {
  const MAX_PHONES_PER_CONTACT = 4;

  const { contacts, loading, error, setContacts, addContact, updateContact, removeContact, setLoading, setError } = useContactStore();
  const { dams, setDams } = useDamStore();
  const { clients, setClients } = useClientStore();
  const { user } = useAuthStore();

  const isAdmin = user?.roles?.includes('admin') || false;

  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<IContact | null>(null);
  const [selectedDamId, setSelectedDamId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterClientId, setFilterClientId] = useState<string>('');
  const [filterDamId, setFilterDamId] = useState<string>('');
  const [filterGroupId, setFilterGroupId] = useState<string>('');
  const [showAdministrativeGroups, setShowAdministrativeGroups] = useState<boolean>(false);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<IContact | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [formData, setFormData] = useState<ICreateContactRequest>({
    name: '',
    phones: [],
    damId: '',
    groupId: '',
  });
  const [phoneInput, setPhoneInput] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+55');

  // Lista de países com códigos de telefone
  const countries = [
    { code: '+55', name: 'Brasil', flag: '🇧🇷', iso: 'br', placeholder: '11987654321', mask: 'XX XXXXX-XXXX' },
    { code: '+1', name: 'Estados Unidos', flag: '🇺🇸', iso: 'us', placeholder: '2025551234', mask: 'XXX XXX-XXXX' },
    { code: '+351', name: 'Portugal', flag: '🇵🇹', iso: 'pt', placeholder: '912345678', mask: 'XXX XXX XXX' },
    { code: '+34', name: 'Espanha', flag: '🇪🇸', iso: 'es', placeholder: '612345678', mask: 'XXX XXX XXX' },
    { code: '+33', name: 'França', flag: '🇫🇷', iso: 'fr', placeholder: '612345678', mask: 'X XX XX XX XX' },
    { code: '+49', name: 'Alemanha', flag: '🇩🇪', iso: 'de', placeholder: '15212345678', mask: 'XXX XXXXXXXX' },
    { code: '+44', name: 'Reino Unido', flag: '🇬🇧', iso: 'gb', placeholder: '7400123456', mask: 'XXXX XXXXXX' },
    { code: '+39', name: 'Itália', flag: '🇮🇹', iso: 'it', placeholder: '3123456789', mask: 'XXX XXX XXXX' },
    { code: '+52', name: 'México', flag: '🇲🇽', iso: 'mx', placeholder: '5512345678', mask: 'XX XXXX XXXX' },
    { code: '+54', name: 'Argentina', flag: '🇦🇷', iso: 'ar', placeholder: '91123456789', mask: 'XX XXXX-XXXX' },
  ];

  // Componente para renderizar bandeira
  const CountryFlag = ({ iso, className = "" }: { iso: string; className?: string }) => (
    <img 
      src={`https://flagcdn.com/w20/${iso}.png`}
      srcSet={`https://flagcdn.com/w40/${iso}.png 2x`}
      width="20"
      height="15"
      alt={iso.toUpperCase()}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [contactsData, damsData, clientsData] = await Promise.all([
        contactService.getAllContacts(),
        damService.getAllDams(),
        clientService.getAllClients(),
      ]);
      setContacts(contactsData);
      setDams(damsData);
      setClients(clientsData);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [setContacts, setDams, setClients, setLoading, setError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (contact: IContact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      phones: contact.phones,
      damId: contact.damId,
      groupId: contact.groupId,
    });
    setSelectedDamId(contact.damId);
    setPhoneInput('');
    setPhoneError('');
    setSelectedCountryCode('+55');
    setShowForm(true);
  };

  const handleDelete = (contact: IContact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;

    try {
      await contactService.deleteContact(contactToDelete.id);
      removeContact(contactToDelete.id);
      setShowDeleteModal(false);
      setContactToDelete(null);
    } catch (err) {
      setError('Erro ao excluir cadastrado');
      console.error(err);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setContactToDelete(null);
  };

  const validateInternationalPhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    
    // Validação específica para Brasil - apenas celulares
    if (cleanPhone.startsWith('+55')) {
      // Brasil: +55 (código) + XX (DDD 2 dígitos) + 9 (celular) + XXXXXXXX (8 dígitos)
      const brazilMobileRegex = /^\+55\d{2}9\d{8}$/;
      
      if (!brazilMobileRegex.test(cleanPhone)) {
        return false;
      }
      
      // Valida DDD
      const ddd = parseInt(cleanPhone.substring(3, 5));
      const validDDDs = [
        11, 12, 13, 14, 15, 16, 17, 18, 19,
        21, 22, 24, 27, 28,
        31, 32, 33, 34, 35, 37, 38,
        41, 42, 43, 44, 45, 46,
        47, 48, 49,
        51, 53, 54, 55,
        61, 62, 64, 63,
        65, 66, 67, 68, 69,
        71, 73, 74, 75, 77, 79,
        81, 87, 82, 83, 84,
        85, 88, 86, 89,
        91, 93, 94, 92, 97,
        95, 96, 98, 99,
      ];
      
      return validDDDs.includes(ddd);
    }
    
    // Para outros países: validação genérica internacional
    // +[código 1-3 dígitos][número 8-15 dígitos]
    const internationalPhoneRegex = /^\+\d{1,3}\d{8,15}$/;
    return internationalPhoneRegex.test(cleanPhone);
  };

  const isBrazilianLandline = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    
    if (!cleanPhone.startsWith('+55')) {
      return false;
    }
    
    // Verifica se tem formato de número brasileiro (DDD + 8 ou 9 dígitos)
    const brazilPhoneRegex = /^\+55\d{2}\d{8,9}$/;
    if (!brazilPhoneRegex.test(cleanPhone)) {
      return false;
    }
    
    // Verifica se NÃO tem o 9 após DDD (indicador de celular)
    const ninthDigit = cleanPhone.charAt(5); // Posição após +55XX
    return ninthDigit !== '9';
  };

  const formatPhoneNumber = (value: string, countryCode: string): string => {
    const numbers = value.replace(/\D/g, '');
    
    if (countryCode === '+55') {
      // Brasil: XX XXXXX-XXXX (celular) ou XX XXXX-XXXX (fixo)
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 6) return `${numbers.slice(0, 2)} ${numbers.slice(2)}`;
      if (numbers.length <= 10) return `${numbers.slice(0, 2)} ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
      return `${numbers.slice(0, 2)} ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
    
    if (countryCode === '+1') {
      // EUA/Canadá: XXX XXX-XXXX
      if (numbers.length <= 3) return numbers;
      if (numbers.length <= 6) return `${numbers.slice(0, 3)} ${numbers.slice(3)}`;
      return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
    
    if (countryCode === '+351') {
      // Portugal: XXX XXX XXX
      if (numbers.length <= 3) return numbers;
      if (numbers.length <= 6) return `${numbers.slice(0, 3)} ${numbers.slice(3)}`;
      return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6, 9)}`;
    }
    
    // Outros países: formatação básica com espaços a cada 4 dígitos
    return numbers.match(/.{1,4}/g)?.join(' ') || numbers;
  };

  const parsePhoneNumber = (fullPhone: string) => {
    const country = countries.find(c => fullPhone.startsWith(c.code));
    if (!country) return { iso: 'un', code: '', number: fullPhone };
    
    const number = fullPhone.substring(country.code.length);
    
    // Formata o número baseado no país
    let formattedNumber = number;
    if (country.code === '+55') {
      // Brasil: XX XXXXX-XXXX (celular) ou XX XXXX-XXXX (fixo)
      if (number.length === 11) {
        formattedNumber = `${number.slice(0, 2)} ${number.slice(2, 7)}-${number.slice(7)}`;
      } else if (number.length === 10) {
        formattedNumber = `${number.slice(0, 2)} ${number.slice(2, 6)}-${number.slice(6)}`;
      }
    } else if (country.code === '+1' && number.length === 10) {
      // EUA: XXX XXX-XXXX
      formattedNumber = `${number.slice(0, 3)} ${number.slice(3, 6)}-${number.slice(6)}`;
    } else if (country.code === '+351' && number.length === 9) {
      // Portugal: XXX XXX XXX
      formattedNumber = `${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
    } else {
      // Formatação genérica: espaços a cada 4 dígitos
      formattedNumber = number.match(/.{1,4}/g)?.join(' ') || number;
    }
    
    return { iso: country.iso, code: country.code, number: formattedNumber };
  };

  const handleAddPhone = () => {
    if (formData.phones.length >= MAX_PHONES_PER_CONTACT) {
      setPhoneError(`Limite máximo de ${MAX_PHONES_PER_CONTACT} números por contato atingido`);
      return;
    }

    if (!phoneInput.trim()) {
      setPhoneError('Digite um número de telefone');
      return;
    }

    const cleanNumber = phoneInput.replace(/\D/g, '');
    const fullPhone = `${selectedCountryCode}${cleanNumber}`;

    // Verifica se é número fixo brasileiro
    if (isBrazilianLandline(fullPhone)) {
      setPhoneError('⚠️ Número fixo não é permitido. Apenas números de celular são aceitos.');
      return;
    }

    // Validação de telefone
    if (!validateInternationalPhone(fullPhone)) {
      if (selectedCountryCode === '+55') {
        setPhoneError('Número brasileiro inválido. Use formato: DDD + 9 + 8 dígitos (apenas celulares)');
      } else {
        setPhoneError('Número inválido. Digite um telefone válido.');
      }
      return;
    }

    // Verifica se já foi adicionado no formulário atual
    if (formData.phones.includes(fullPhone)) {
      setPhoneError('Este número já foi adicionado neste contato');
      return;
    }

    // Verifica se o número já existe em outros contatos
    const existingContact = contacts.find(contact => 
      contact.phones.includes(fullPhone) && 
      (!editingContact || contact.id !== editingContact.id)
    );

    if (existingContact) {
      setPhoneError(`Este número já está cadastrado no contato: ${existingContact.name}`);
      return;
    }

    setFormData({ ...formData, phones: [...formData.phones, fullPhone] });
    setPhoneInput('');
    setPhoneError('');
  };

  const handleRemovePhone = (phoneToRemove: string) => {
    setFormData({ ...formData, phones: formData.phones.filter(p => p !== phoneToRemove) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.phones.length > MAX_PHONES_PER_CONTACT) {
      setError(`O contato pode ter no máximo ${MAX_PHONES_PER_CONTACT} números de telefone.`);
      return;
    }
    
    // Se houver número digitado mas não adicionado, adiciona automaticamente
    if (phoneInput.trim()) {
      const cleanNumber = phoneInput.replace(/\D/g, '');
      const fullPhone = `${selectedCountryCode}${cleanNumber}`;

      // Verifica se é número fixo brasileiro
      if (isBrazilianLandline(fullPhone)) {
        setPhoneError('⚠️ Número fixo não é permitido. Apenas números de celular são aceitos.');
        return;
      }

      // Validação de telefone
      if (!validateInternationalPhone(fullPhone)) {
        if (selectedCountryCode === '+55') {
          setPhoneError('Número brasileiro inválido. Use formato: DDD + 9 + 8 dígitos (apenas celulares)');
        } else {
          setPhoneError('Número inválido. Digite um telefone válido.');
        }
        return;
      }

      // Verifica duplicados
      const existingContact = contacts.find(contact => 
        contact.phones.includes(fullPhone) && 
        (!editingContact || contact.id !== editingContact.id)
      );

      if (existingContact) {
        setPhoneError(`Este número já está cadastrado no contato: ${existingContact.name}`);
        return;
      }

      if (!formData.phones.includes(fullPhone) && formData.phones.length >= MAX_PHONES_PER_CONTACT) {
        setPhoneError(`Limite máximo de ${MAX_PHONES_PER_CONTACT} números por contato atingido`);
        return;
      }

      // Adiciona o número automaticamente
      if (!formData.phones.includes(fullPhone)) {
        formData.phones = [...formData.phones, fullPhone];
        setPhoneInput('');
        setPhoneError('');
      }
    }
    
    if (!formData.name.trim() || formData.phones.length === 0 || !formData.damId || !formData.groupId) {
      setError('Todos os campos obrigatórios devem ser preenchidos. Adicione pelo menos um número de telefone.');
      return;
    }

    try {
      setLoading(true);
      if (editingContact) {
        const updated = await contactService.updateContact(editingContact.id, {
          name: formData.name,
          phones: formData.phones,
          damId: formData.damId,
          groupId: formData.groupId,
        });
        updateContact(editingContact.id, updated);
      } else {
        const created = await contactService.createContact(formData);
        addContact(created);
      }
      handleCloseForm();
      setError(null);
    } catch (err: any) {
      const errorMessage = err?.message || 'Erro ao salvar cadastrado';
      setError(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingContact(null);
    setSelectedDamId('');
    setFormData({ name: '', phones: [], damId: '', groupId: '' });
    setPhoneInput('');
    setPhoneError('');
    setSelectedCountryCode('+55');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterClientId('');
    setFilterDamId('');
    setFilterGroupId('');
  };

  const isAdministrativeGroup = (groupSigla?: string) =>
    !!groupSigla && (
      groupSigla.startsWith('Gestores - ') ||
      groupSigla.startsWith('Colaboradores - ')
    );

  const hasActiveFilters = () => {
    return !!(searchTerm || filterClientId || filterDamId || filterGroupId);
  };

  const handleExportClick = () => {
    if (hasActiveFilters()) {
      setShowExportModal(true);
    } else {
      performExport(false);
    }
  };

  const performExport = (useFilters: boolean) => {
    const escapeCSV = (value: any): string => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(';') || stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const formatSpreadsheetText = (value: string): string => {
      if (!value) return '';
      return `="${value}"`;
    };

    const contactsToExport = useFilters ? filteredContacts : contacts;
    const headers = ['Nome', 'País', 'Número', 'Barragem', 'Grupo'];
    
    // Cria uma linha para cada telefone de cada contato
    const rows: string[][] = [];
    contactsToExport.forEach(contact => {
      contact.phones.forEach(phone => {
        // Separa código do país e número
        const country = countries.find(c => phone.startsWith(c.code));
        const countryCode = country?.code || '+55';
        const phoneNumber = phone.substring(countryCode.length);
        
        rows.push([
          escapeCSV(contact.name),
          escapeCSV(formatSpreadsheetText(countryCode)),
          escapeCSV(formatSpreadsheetText(phoneNumber)),
          escapeCSV(contact.damName),
          escapeCSV(contact.groupSigla)
        ]);
      });
    });

    const csvContent = [headers, ...rows].map(row => row.join(';')).join('\r\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filterSuffix = useFilters ? '-filtrados' : '';
    a.download = `contatos${filterSuffix}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        setLoading(true);
        let text = event.target?.result as string;
        
        // Remove BOM UTF-8 se presente
        if (text.charCodeAt(0) === 0xFEFF) {
          text = text.substring(1);
        }

        // Separa linhas e remove header
        const lines = text.split(/\r?\n/).filter(line => line.trim());
        if (lines.length < 2) {
          setError('Arquivo CSV vazio ou inválido');
          return;
        }

        const headers = lines[0].split(';');
        if (headers.length !== 5 || headers[0] !== 'Nome' || headers[1] !== 'País' || headers[2] !== 'Número' || headers[3] !== 'Barragem' || headers[4] !== 'Grupo') {
          setError('Formato inválido. Use o mesmo formato do CSV exportado (Nome;País;Número;Barragem;Grupo)');
          return;
        }

        const rows = lines.slice(1);
        const errors: string[] = [];
        
        // Estrutura intermediária: agrupa telefones por nome
        interface ContactRow {
          name: string;
          phones: string[];
          phoneErrors: string[];
          damName: string;
          groupSigla: string;
          lineNumbers: number[];
        }
        
        const contactMap = new Map<string, ContactRow>();

        for (let i = 0; i < rows.length; i++) {
          const line = rows[i];
          const parts: string[] = [];
          let current = '';
          let inQuotes = false;

          // Parser CSV com suporte a campos entre aspas
          for (let j = 0; j < line.length; j++) {
            const char = line[j];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ';' && !inQuotes) {
              parts.push(current);
              current = '';
            } else {
              current += char;
            }
          }
          parts.push(current);

          if (parts.length !== 5) {
            errors.push(`Linha ${i + 2}: número incorreto de colunas (esperado 5, encontrado ${parts.length})`);
            continue;
          }

          let [name, countryCode, phoneNumber, damName, groupSigla] = parts.map(p => p.trim());

          // Remove aspas externas se presentes
          name = name.replace(/^"(.*)"$/, '$1');
          countryCode = countryCode.replace(/^"(.*)"$/, '$1');
          phoneNumber = phoneNumber.replace(/^"(.*)"$/, '$1');
          damName = damName.replace(/^"(.*)"$/, '$1');
          groupSigla = groupSigla.replace(/^"(.*)"$/, '$1');

          // Normaliza campos exportados como texto de planilha (ex.: ="+55")
          countryCode = countryCode.replace(/^="(.*)"$/, '$1');
          phoneNumber = phoneNumber.replace(/^="(.*)"$/, '$1');

          if (!name || !countryCode || !phoneNumber || !damName || !groupSigla) {
            errors.push(`Linha ${i + 2}: campos obrigatórios vazios`);
            continue;
          }

          // Monta o telefone completo
          const fullPhone = `${countryCode}${phoneNumber.replace(/\D/g, '')}`;
          
          // Cria ou atualiza entrada no mapa
          const key = `${name.toLowerCase()}|${damName.toLowerCase()}|${groupSigla.toLowerCase()}`;
          
          if (!contactMap.has(key)) {
            contactMap.set(key, {
              name,
              phones: [],
              phoneErrors: [],
              damName,
              groupSigla,
              lineNumbers: []
            });
          }
          
          const contactRow = contactMap.get(key)!;
          contactRow.lineNumbers.push(i + 2);
          
          // Validações do telefone
          let phoneError = '';
          
          // Verifica se é número fixo brasileiro
          if (isBrazilianLandline(fullPhone)) {
            phoneError = `⚠️ Número fixo não permitido: ${fullPhone}`;
          }
          // Valida formato
          else if (!validateInternationalPhone(fullPhone)) {
            phoneError = countryCode === '+55' 
              ? `Número brasileiro inválido: ${fullPhone} (use DDD + 9 + 8 dígitos)` 
              : `Número inválido: ${fullPhone}`;
          }
          // Verifica duplicados dentro do mesmo contato
          else if (contactRow.phones.includes(fullPhone)) {
            phoneError = `Número duplicado: ${fullPhone}`;
          }
          // Verifica limite máximo de números por contato
          else if (contactRow.phones.length >= MAX_PHONES_PER_CONTACT) {
            phoneError = `Limite máximo de ${MAX_PHONES_PER_CONTACT} números por contato excedido`;
          }
          // Verifica se existe em outro contato
          else {
            const existingContact = contacts.find(c => c.phones.includes(fullPhone));
            if (existingContact) {
              phoneError = `Número já cadastrado no contato "${existingContact.name}": ${fullPhone}`;
            }
          }
          
          if (phoneError) {
            contactRow.phoneErrors.push(`Linha ${i + 2}: ${phoneError}`);
          } else {
            contactRow.phones.push(fullPhone);
          }
        }

        // Converte mapa em array de contatos para importar
        const contactsToImport: ICreateContactRequest[] = [];
        
        Array.from(contactMap.entries()).forEach(([key, contactRow]) => {
          // Se tem erros, reporta mas não impede importação dos números válidos
          if (contactRow.phoneErrors.length > 0) {
            errors.push(...contactRow.phoneErrors);
          }
          
          // Se não tem nenhum telefone válido, pula
          if (contactRow.phones.length === 0) {
            errors.push(`Contato "${contactRow.name}" (linhas ${contactRow.lineNumbers.join(', ')}): nenhum telefone válido`);
            return;
          }

          // Encontra a barragem pelo nome
          const dam = dams.find(d => d.name.toLowerCase() === contactRow.damName.toLowerCase());
          if (!dam) {
            errors.push(`Contato "${contactRow.name}" (linhas ${contactRow.lineNumbers.join(', ')}): Barragem "${contactRow.damName}" não encontrada`);
            return;
          }

          // Encontra o grupo pela sigla dentro da barragem
          const group = dam.groups?.find(g => g.sigla.toLowerCase() === contactRow.groupSigla.toLowerCase());
          if (!group) {
            errors.push(`Contato "${contactRow.name}" (linhas ${contactRow.lineNumbers.join(', ')}): Grupo "${contactRow.groupSigla}" não encontrado na barragem "${contactRow.damName}"`);
            return;
          }

          contactsToImport.push({
            name: contactRow.name,
            phones: contactRow.phones,
            damId: dam.id,
            groupId: group.id
          });
        });

        if (contactsToImport.length === 0) {
          setError(errors.length > 0 ? errors.join('\n') : 'Nenhum contato válido encontrado');
          return;
        }

        // Importa os contatos
        let successCount = 0;
        let failCount = 0;

        for (const contactData of contactsToImport) {
          try {
            const created = await contactService.createContact(contactData);
            addContact(created);
            successCount++;
          } catch (err: any) {
            failCount++;
            const errorMsg = err?.message || String(err);
            errors.push(`Erro ao criar "${contactData.name}": ${errorMsg}`);
          }
        }

        // Mostra resultado
        if (successCount > 0 && failCount === 0) {
          setError(null);
          alert(`✅ ${successCount} contato(s) importado(s) com sucesso!`);
        } else if (successCount > 0 && failCount > 0) {
          setError(`Importação parcial: ${successCount} sucesso(s), ${failCount} erro(s).\n${errors.slice(-5).join('\n')}`);
        } else {
          setError(`Falha na importação:\n${errors.slice(-10).join('\n')}`);
        }

      } catch (err) {
        setError('Erro ao processar arquivo CSV');
        console.error(err);
      } finally {
        setLoading(false);
        // Limpa o input para permitir reimportação do mesmo arquivo
        e.target.value = '';
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleDeleteSelected = () => {
    if (selectedContacts.length === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setLoading(true);
      await Promise.all(selectedContacts.map(id => contactService.deleteContact(id)));
      selectedContacts.forEach(id => removeContact(id));
      setSelectedContacts([]);
      setShowBulkDeleteModal(false);
      setError(null);
    } catch (err) {
      setError('Erro ao excluir contatos selecionados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredContacts.map(c => c.id));
    }
  };

  const modeScopedContacts = contacts.filter((contact) => {
    const adminContact = isAdministrativeGroup(contact.groupSigla);
    return showAdministrativeGroups ? adminContact : !adminContact;
  });

  const filteredContacts = modeScopedContacts.filter((contact) => {
    const matchesSearch = !searchTerm || 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phones.some(phone => phone.includes(searchTerm));
    
    // Filtro de cliente: busca o dam do contato e verifica se pertence ao cliente filtrado
    let matchesClient = true;
    if (filterClientId) {
      const contactDam = dams.find(d => d.id === contact.damId);
      matchesClient = contactDam?.clientId === filterClientId;
    }
    
    const matchesDam = !filterDamId || contact.damId === filterDamId;
    const matchesGroup = !filterGroupId || contact.groupId === filterGroupId;
    return matchesSearch && matchesClient && matchesDam && matchesGroup;
  });

  const selectedDam = dams.find((d) => d.id === selectedDamId);
  const damGroups = (selectedDam?.groups || []).filter((group) =>
    showAdministrativeGroups ? isAdministrativeGroup(group.sigla) : !isAdministrativeGroup(group.sigla),
  );
  
  // Barragens disponíveis para filtro (filtradas por cliente se houver filtro de cliente)
  const availableDams = filterClientId 
    ? dams.filter(d => d.clientId === filterClientId)
    : dams;
  
  const filterDam = dams.find((d) => d.id === filterDamId);
  const filterGroups = (filterDam?.groups || []).filter((group) =>
    showAdministrativeGroups ? isAdministrativeGroup(group.sigla) : !isAdministrativeGroup(group.sigla),
  );

  if (loading && !contacts.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando cadastrados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <UserCheck className="h-8 w-8 text-blue-600" />
            Gerenciamento de Contatos
          </h1>
          <p className="text-slate-600 mt-2">
            {showAdministrativeGroups
              ? 'Visualizando contatos administrativos'
              : 'Organize e gerencie contatos em massa'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showAdministrativeGroups ? 'default' : 'outline'}
            className="h-11"
            onClick={() => {
              setShowAdministrativeGroups((prev) => !prev);
              setSelectedContacts([]);
              setFilterDamId('');
              setFilterGroupId('');
            }}
          >
            <Users className="h-5 w-5 mr-2" />
            {showAdministrativeGroups ? 'Ver Contatos Comuns' : 'Ver Contatos Administrativos'}
          </Button>
          <label htmlFor="import-csv">
            <Button variant="outline" className="h-11" onClick={() => document.getElementById('import-csv')?.click()}>
              <Upload className="h-5 w-5 mr-2" />
              Importar CSV
            </Button>
            <input
              id="import-csv"
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              className="hidden"
            />
          </label>
          <Button onClick={handleExportClick} variant="outline" className="h-11">
            <Download className="h-5 w-5 mr-2" />
            Exportar CSV
          </Button>
          <Button onClick={() => setShowForm(true)} className="h-11">
            <Plus className="h-5 w-5 mr-2" />
            Novo Contato
          </Button>
        </div>
      </div>

      {/* Alertas/Erros */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>✕</Button>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
          </div>
          {(searchTerm || filterClientId || filterDamId || filterGroupId) && (
            <Button onClick={clearFilters} variant="outline" size="sm" className="text-slate-600">
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <Label htmlFor="searchTerm" className="text-sm font-medium text-slate-700">
              Pesquisar
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome ou telefone..."
                className="h-11 pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filterDam" className="text-sm font-medium text-slate-700">
              Filtrar por Barragem
            </Label>
            <div className="flex gap-2">
              <Select value={filterDamId} onValueChange={(value) => {
                setFilterDamId(value);
                setFilterGroupId('');
              }}>
                <SelectTrigger id="filterDam" className="flex-1 h-11">
                  <SelectValue placeholder="Todas as barragens" />
                </SelectTrigger>
                <SelectContent>
                  {availableDams.map((dam) => (
                    <SelectItem key={dam.id} value={dam.id}>
                      {dam.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterDamId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilterDamId('');
                    setFilterGroupId('');
                  }}
                  className="px-3"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filterGroup" className="text-sm font-medium text-slate-700">
              Filtrar por Grupo
            </Label>
            <div className="flex gap-2">
              <Select 
                value={filterGroupId} 
                onValueChange={setFilterGroupId}
                disabled={!filterDamId}
              >
                <SelectTrigger id="filterGroup" className="flex-1 h-11">
                  <SelectValue placeholder="Todos os grupos" />
                </SelectTrigger>
                <SelectContent>
                  {filterGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.sigla}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterGroupId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilterGroupId('')}
                  className="px-3"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filterClient" className="text-sm font-medium text-slate-700">
              Filtrar por Cliente {!isAdmin && '(Somente Admin)'}
            </Label>
            <div className="flex gap-2">
              <Select 
                value={filterClientId} 
                onValueChange={(value) => {
                  setFilterClientId(value);
                  setFilterDamId('');
                  setFilterGroupId('');
                }}
                disabled={!isAdmin}
              >
                <SelectTrigger id="filterClient" className="flex-1 h-11" disabled={!isAdmin}>
                  <SelectValue placeholder={isAdmin ? "Todos os clientes" : "Bloqueado"} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterClientId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilterClientId('');
                    setFilterDamId('');
                    setFilterGroupId('');
                  }}
                  className="px-3"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Tabela de Contatos */}
      <Card>
        <CardContent className="p-6">
          {/* Ações em massa */}
          {selectedContacts.length > 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-blue-700">
                <CheckSquare className="h-4 w-4" />
                <span className="font-medium">{selectedContacts.length} contato(s) selecionado(s)</span>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDeleteSelected}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir Selecionados
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedContacts([])}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {modeScopedContacts.length === 0 ? 'Nenhum contato cadastrado' : 'Nenhum contato encontrado'}
              </h3>
              <p className="text-slate-600 mb-4">
                {modeScopedContacts.length === 0 
                  ? 'Comece adicionando contatos individualmente ou importe um arquivo CSV'
                  : 'Tente ajustar os filtros de pesquisa'}
              </p>
              {modeScopedContacts.length === 0 && (
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => setShowForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Primeiro Contato
                  </Button>
                  <label htmlFor="import-csv-empty">
                    <Button variant="outline" onClick={() => document.getElementById('import-csv-empty')?.click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      Importar CSV
                    </Button>
                    <input
                      id="import-csv-empty"
                      type="file"
                      accept=".csv"
                      onChange={handleImportCSV}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 max-h-[62vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={selectedContacts.length === filteredContacts.length}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700">Nome</TableHead>
                    <TableHead className="font-semibold text-slate-700">Telefone</TableHead>
                    <TableHead className="font-semibold text-slate-700">Barragem</TableHead>
                    <TableHead className="font-semibold text-slate-700">Grupo</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.map((contact) => {
                    const isSelected = selectedContacts.includes(contact.id);
                    return (
                      <TableRow 
                        key={contact.id} 
                        className={`hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setSelectedContacts(prev =>
                                prev.includes(contact.id)
                                  ? prev.filter(id => id !== contact.id)
                                  : [...prev, contact.id]
                              );
                            }}
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </TableCell>
                        <TableCell className="font-semibold text-slate-900">{contact.name}</TableCell>
                        <TableCell className="text-slate-600">
                          <div className="flex flex-col gap-1.5">
                            {contact.phones.map((phone, idx) => {
                              const { iso, code, number } = parsePhoneNumber(phone);
                              return (
                                <div key={idx} className="flex items-center gap-2">
                                  <CountryFlag iso={iso} />
                                  <span className="text-xs font-semibold text-slate-600">{code}</span>
                                  <div className="h-3 w-px bg-slate-300"></div>
                                  <span className="text-sm text-slate-700">{number}</span>
                                </div>
                              );
                            })}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-normal">
                            {contact.damName}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-normal">
                            {contact.groupSigla}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleEdit(contact)}
                              className="hover:bg-blue-50 hover:text-blue-600"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDelete(contact)}
                              className="hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              {editingContact ? (
                <>
                  <Edit2 className="h-5 w-5 text-blue-600" />
                  Editar Contato
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 text-blue-600" />
                  Novo Contato
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Nome Completo *
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Digite o nome completo"
                required
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">
                Telefones * ({formData.phones.length}/{MAX_PHONES_PER_CONTACT})
              </Label>
              
              {/* Lista de telefones adicionados */}
              {formData.phones.length > 0 && (
                <div className="space-y-2 mb-3">
                  {formData.phones.map((phone, index) => {
                    const { iso, code, number } = parsePhoneNumber(phone);
                    return (
                      <div key={index} className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <CountryFlag iso={iso} />
                          <span className="text-sm font-semibold text-blue-700">{code}</span>
                        </div>
                        <div className="h-6 w-px bg-blue-300"></div>
                        <Phone className="h-4 w-4 text-blue-600" />
                        <span className="flex-1 text-sm font-medium text-slate-700">{number}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePhone(phone)}
                          className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Campo de entrada para adicionar novo telefone */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  {/* Select de País Compacto */}
                  <Select value={selectedCountryCode} onValueChange={setSelectedCountryCode}>
                    <SelectTrigger className="w-[130px] h-11">
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <CountryFlag iso={countries.find(c => c.code === selectedCountryCode)?.iso || 'br'} />
                          <span className="font-medium">{selectedCountryCode}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          <div className="flex items-center gap-2">
                            <CountryFlag iso={country.iso} />
                            <span className="text-sm">{country.name}</span>
                            <span className="text-slate-500 text-xs font-medium">{country.code}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Input de Número */}
                  <Input
                    id="phone"
                    type="tel"
                    value={formatPhoneNumber(phoneInput, selectedCountryCode)}
                    onChange={(e) => {
                      const numbers = e.target.value.replace(/\D/g, '');
                      setPhoneInput(numbers);
                      setPhoneError('');
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPhone();
                      }
                    }}
                    placeholder={countries.find(c => c.code === selectedCountryCode)?.placeholder || '11987654321'}
                    className="flex-1 h-11"
                    disabled={formData.phones.length >= MAX_PHONES_PER_CONTACT}
                  />
                </div>
                
                <Button
                  type="button"
                  onClick={handleAddPhone}
                  variant="outline"
                  className="w-full h-10"
                  disabled={formData.phones.length >= MAX_PHONES_PER_CONTACT}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {formData.phones.length >= MAX_PHONES_PER_CONTACT ? 'Limite de telefones atingido' : 'Adicionar Telefone'}
                </Button>
              </div>
              
              {phoneError && (
                <p className="text-xs text-red-600 mt-1">{phoneError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="damId" className="text-sm font-medium">
                Barragem *
              </Label>
              <Select
                value={selectedDamId}
                onValueChange={(value) => {
                  setSelectedDamId(value);
                  setFormData({ ...formData, damId: value, groupId: '' });
                }}
              >
                <SelectTrigger id="damId" className="h-11">
                  <SelectValue placeholder="Selecione a barragem" />
                </SelectTrigger>
                <SelectContent>
                  {dams.map((dam) => (
                    <SelectItem key={dam.id} value={dam.id}>
                      {dam.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="groupId" className="text-sm font-medium">
                Grupo *
              </Label>
              <Select
                value={formData.groupId}
                onValueChange={(value) => setFormData({ ...formData, groupId: value })}
                disabled={!selectedDamId}
              >
                <SelectTrigger id="groupId" className="h-11">
                  <SelectValue placeholder={selectedDamId ? "Selecione o grupo" : "Primeiro selecione uma barragem"} />
                </SelectTrigger>
                <SelectContent>
                  {damGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.sigla}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="min-w-[120px]">
                {loading ? 'Salvando...' : editingContact ? 'Atualizar Contato' : 'Criar Contato'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão Individual */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle className="text-xl">Confirmar Exclusão</DialogTitle>
            </div>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-slate-700">
              Tem certeza que deseja excluir o contato{' '}
              <span className="font-semibold">{contactToDelete?.name}</span>?
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Esta ação não poderá ser desfeita.
            </p>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={cancelDelete} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={loading}
              className="h-11 bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão em Massa */}
      <Dialog open={showBulkDeleteModal} onOpenChange={setShowBulkDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle className="text-xl">Confirmar Exclusão em Massa</DialogTitle>
            </div>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-slate-700">
              Tem certeza que deseja excluir{' '}
              <span className="font-semibold">{selectedContacts.length} contato(s)</span> selecionado(s)?
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Esta ação não poderá ser desfeita.
            </p>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={cancelBulkDelete} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmBulkDelete}
              disabled={loading}
              className="h-11 bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Excluindo...' : `Excluir ${selectedContacts.length} contato(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Exportação */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Download className="h-5 w-5 text-blue-600" />
              </div>
              <DialogTitle className="text-xl">Exportar Contatos</DialogTitle>
            </div>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-700">
              Você possui filtros ativos. Como deseja exportar os contatos?
            </p>
            
            <div className="space-y-3">
              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                <div className="flex items-start gap-3">
                  <Filter className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Apenas filtrados</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Exportar {filteredContacts.length} contato(s) que correspondem aos filtros ativos
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 border border-slate-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <UserCheck className="h-5 w-5 text-slate-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">Todos os contatos</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Exportar todos os {contacts.length} contato(s) cadastrados, ignorando filtros
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setShowExportModal(false)} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => performExport(true)}
              variant="outline"
              className="h-11"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtrados ({filteredContacts.length})
            </Button>
            <Button
              type="button"
              onClick={() => performExport(false)}
              className="h-11"
            >
              <Download className="h-4 w-4 mr-2" />
              Todos ({contacts.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
